import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Wrap = styled.section`
  padding: 5rem 0;
  background: var(--bg-2);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);

  @media (min-width: 900px) { padding: 7rem 0; }
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Head = styled.div`
  max-width: 720px;
  margin: 0 auto 3.5rem;
  text-align: center;
`;

const Eyebrow = styled.div`
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.6rem);
  line-height: 1.0;
  letter-spacing: -0.032em;
  margin: 0;
  color: var(--ink);

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  font-family: var(--sans);
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 1.2rem auto 0;
  max-width: 50ch;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
`;

const Card = styled(motion.article)`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 18px;
  padding: 2rem 1.9rem;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-card);
  transition: border-color 280ms ease, box-shadow 320ms ease;

  &:hover {
    border-color: var(--rule-2);
    box-shadow: var(--shadow-card-hover);
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 3px;
  margin-bottom: 1.25rem;
  color: var(--warning);

  svg { width: 16px; height: 16px; }
`;

const Quote = styled.blockquote`
  font-family: var(--sans);
  font-size: 1rem;
  line-height: 1.62;
  color: var(--ink-2);
  margin: 0;
  flex: 1 1 auto;

  strong { font-weight: 600; color: var(--ink); }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.6rem;
  padding-top: 1.4rem;
  border-top: 1px solid var(--rule);
`;

const Person = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .n {
    font-family: var(--sans);
    font-weight: 600;
    font-size: 0.95rem;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
  .r {
    font-family: var(--mono);
    font-size: 0.66rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Logo = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  img {
    height: 34px;
    width: auto;
    max-width: 110px;
    object-fit: contain;
    display: block;
    opacity: 0.9;
  }
`;

function StarRow() {
  return (
    <Stars aria-label="5 de 5 estrellas">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.1 6.47L12 17.9l-5.8 3.07 1.1-6.47L2.6 9.35l6.5-.95L12 2.5z" />
        </svg>
      ))}
    </Stars>
  );
}

const REVIEWS = [
  {
    quote: (
      <>
        Cliente Recurrente ha sido un aliado estratégico para la recaptura y
        fidelización. En el rubro de mascotas, los clientes suelen ser infieles
        a las tiendas, pero gracias a esta herramienta{" "}
        <strong>incrementamos la fidelización entre 10 y 15 puntos.</strong>
      </>
    ),
    name: "Ariel Sandoval",
    role: "CEO · lamascota.cl",
    logo: "https://images.jumpseller.com/store/la-mascota/store/logo/Logo_20Cuadrado.png?1757443070",
    logoAlt: "La Mascota",
  },
  {
    quote: (
      <>
        Es un sistema automatizado con el que recuperé clientes perdidos y
        aumenté ventas sin esfuerzo. Reactivé a 33 clientes inactivos y generé
        compras de 115 clientes frecuentes, sumando{" "}
        <strong>más de 2.5 millones en ventas adicionales.</strong>
      </>
    ),
    name: "Álvaro Badilla",
    role: "CEO · moonlitter.cl",
    logo: "https://moonlitter.cl/cdn/shop/files/Logo45.jpg?v=1749780764",
    logoAlt: "MoonLitter",
  },
  {
    quote: (
      <>
        La automatización del contacto post-venta de Cliente Recurrente eleva el
        estándar de nuestra marca sin desgastar al equipo ni depender de
        contrataciones que arriesguen la escalabilidad del negocio. Nos entrega
        la tranquilidad de que{" "}
        <strong>el 100% de los clientes son contactados en piloto automático</strong>,
        permitiendo que el equipo humano intervenga solo cuando es realmente
        necesario.
      </>
    ),
    name: "Manuel Bustos",
    role: "Fundador · misterpet.cl",
    logo: "https://images.jumpseller.com/store/misterpet-cl/store/logo/Logo_MisterPet.cl__.png?1741210014",
    logoAlt: "MisterPet",
  },
];

const reveal = (i) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
});

export default function Reviews() {
  return (
    <Wrap id="resenas">
      <Inner>
        <Head>
          <Eyebrow>Reseñas</Eyebrow>
          <Title>
            Marcas que ya venden <span className="accent">más de una vez.</span>
          </Title>
          <Sub>
            E-commerces de mascotas que automatizaron su recompra con Cliente
            Recurrente.
          </Sub>
        </Head>

        <Grid>
          {REVIEWS.map((r, i) => (
            <Card key={r.name} {...reveal(i)}>
              <StarRow />
              <Quote>{r.quote}</Quote>
              <Foot>
                <Person>
                  <span className="n">{r.name}</span>
                  <span className="r">{r.role}</span>
                </Person>
                <Logo>
                  <img src={r.logo} alt={r.logoAlt} loading="lazy" />
                </Logo>
              </Foot>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Wrap>
  );
}
